<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useIntervalFn } from "@vueuse/core";
import { useSystemTimeStore } from "@/store/systemTime";

interface Props {
  /** 活动结束时间戳（毫秒） */
  targetTime: number;
}

const props = defineProps<Props>();

const systemTimeStore = useSystemTimeStore();
/** 服务端时间与本地时间的偏移量，用于在 tick 间推算对齐后的当前时刻 */
const serverTimeOffsetMs = ref(0);

const remainingMs = ref(0);

const normalizeServerTimestamp = (timestamp: number) => {
  if (!timestamp) return 0;
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
};

function syncServerTimeOffset() {
  const serverTimestampMs = normalizeServerTimestamp(systemTimeStore.timestamp);
  if (!serverTimestampMs) return;
  serverTimeOffsetMs.value = serverTimestampMs - Date.now();
}

function getAlignedNow() {
  return Date.now() + serverTimeOffsetMs.value;
}

const pad2 = (value: number) => value.toString().padStart(2, "0");

const countdownParts = computed(() => {
  const ms = Math.max(0, remainingMs.value);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  const centiseconds = Math.floor((ms % 1000) / 10);

  return [
    { type: "box" as const, value: pad2(days) },
    { type: "label" as const, value: "天" },
    { type: "box" as const, value: pad2(hours) },
    { type: "label" as const, value: "时" },
    { type: "box" as const, value: pad2(minutes) },
    { type: "label" as const, value: "分" },
    { type: "box" as const, value: pad2(seconds) },
    { type: "label" as const, value: "秒" },
    { type: "box" as const, value: pad2(centiseconds).charAt(0) },
    // { type: "label" as const, value: "毫秒" }
  ];
});

function syncRemaining() {
  remainingMs.value = Math.max(0, props.targetTime - getAlignedNow());
}

const { pause, resume } = useIntervalFn(syncRemaining, 50, { immediate: false });

watch(
  () => systemTimeStore.timestamp,
  () => syncServerTimeOffset()
);

onMounted(async () => {
  await systemTimeStore.getServerTime();
  syncServerTimeOffset();
  syncRemaining();
  resume();
});

onBeforeUnmount(() => {
  pause();
});
</script>

<template>
  <div class="promotion-618-countdown">
    <template v-for="(part, index) in countdownParts" :key="index">
      <span v-if="part.type === 'box'" class="countdown-box">{{ part.value }}</span>
      <span v-else class="countdown-label">{{ part.value }}</span>
    </template>
  </div>
</template>

<style scoped lang="less">
.promotion-618-countdown {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.countdown-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  flex: none;
  height: 24px;
  padding: 0 4px;
  border-radius: 6px;
  background: #fff;
  color: #070928;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  font-family: "DingTalk JinBuTi", "PingFang SC", sans-serif;
}

.countdown-label {
  flex: none;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  font-family: "DingTalk JinBuTi", "PingFang SC", sans-serif;
}
</style>
